import React from 'react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import DatabaseIcon from '@atlaskit/icon/core/database';
import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import PageIcon from '@atlaskit/icon/core/page';
import QuotationMarkIcon from '@atlaskit/icon/core/quotation-mark';
import SmartLinkIcon from '@atlaskit/icon/core/smart-link';
import WhiteboardIcon from '@atlaskit/icon/core/whiteboard';
import AiGenerativeSlidesIcon from '@atlaskit/icon-lab/core/ai-generative-slides';
import PageLiveDocIcon from '@atlaskit/icon-lab/core/page-live-doc';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { token } from '@atlaskit/tokens';

import type { NestedAdfRendererWrapper } from '@confluence/macro-utils/entry-points/nestedAdfRenderer.ts';

import { ContentType } from '../contentByLabelTypes.ts';
import type { ContentByLabelItem as ContentItemData } from '../contentByLabelTypes.ts';
import { i18n } from '../contentByLabelMacroI18n.ts';
import type { ExcerptType } from '../hooks/useParsedMacroParams.ts';

import { ContentExcerpt, hasExcerptContent } from './ContentExcerpt.tsx';
import { LabelList } from './LabelList.tsx';

const styles = cssMap({
	listItem: {
		marginBlockStart: token('space.100'),
	},
	listItemFirst: {
		marginBlockStart: token('space.0'),
	},
	details: {
		paddingInlineStart: token('space.300'),
	},
	spaceInfo: {
		color: token('color.text.subtlest'),
	},
	contentTypeIcon: {
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	},
	titleLink: {
		textDecoration: 'none',
	},
});

interface ContentEntryProps {
	item: ContentItemData;
	showLabels: boolean;
	showSpace: boolean;
	excerptType: ExcerptType;
	limitLabelLinksToSpace: boolean;
	isFirst: boolean;
	RendererWrapper: NestedAdfRendererWrapper;
}

export const ContentEntry = function ContentEntry({
	item,
	showLabels,
	showSpace,
	excerptType,
	limitLabelLinksToSpace,
	isFirst,
	RendererWrapper,
}: ContentEntryProps): React.JSX.Element {
	const { formatMessage } = useIntl();

	const excerptDocument = hasExcerptContent(excerptType, item.excerpt);

	const getContentTypeIcon = () => {
		if (item.type === ContentType.Blogpost) {
			return <QuotationMarkIcon label="" />;
		}
		if (item.type === ContentType.LivePage) {
			return <PageLiveDocIcon label="" />;
		}
		if (item.type === ContentType.Whiteboard) {
			return <WhiteboardIcon label="" testId="content-type-glyph-whiteboard" />;
		}
		if (item.type === ContentType.Database) {
			return <DatabaseIcon label="" testId="content-type-glyph-database" />;
		}
		if (item.type === ContentType.Embed) {
			return <SmartLinkIcon label="" testId="content-type-glyph-embed" />;
		}
		if (item.type === ContentType.Folder) {
			return <FolderClosedIcon label="" testId="content-type-glyph-folder" />;
		}
		if (item.type === ContentType.Slide) {
			return <AiGenerativeSlidesIcon label="" testId="content-type-glyph-slide" />;
		}
		return <PageIcon label="" />;
	};
	const contentTypeIcon = getContentTypeIcon();

	return (
		<Box
			as="li"
			xcss={isFirst ? styles.listItemFirst : styles.listItem}
			testId={`content-entry-${item.id}`}
		>
			<Inline space="space.050" alignBlock="center" testId="content-by-label-inline">
				<Box xcss={styles.contentTypeIcon} testId={`content-type-icon-${item.type}`}>
					{contentTypeIcon}
				</Box>
				<Anchor href={item.url} xcss={styles.titleLink}>
					{item.title}
				</Anchor>
				{showSpace && (
					<Box as="span" xcss={styles.spaceInfo}>
						{formatMessage(i18n.spaceLabel, { spaceName: item.space.name })}
					</Box>
				)}
				{excerptDocument && (
					<Box as="span" xcss={styles.spaceInfo}>
						{formatMessage(i18n.excerptSeparator)}
					</Box>
				)}
			</Inline>

			<Box xcss={styles.details}>
				{excerptDocument && (
					<ContentExcerpt
						adfDocument={excerptDocument}
						mediaToken={item.mediaToken}
						excerptType={excerptType}
						contentId={item.id}
						spaceKey={item.space.key}
						spaceId={item.space.id}
						RendererWrapper={RendererWrapper}
					/>
				)}

				{showLabels && item.labels.length > 0 && (
					<Box paddingBlockStart="space.050">
						<LabelList
							labels={item.labels}
							spaceKey={item.space.key}
							limitLabelLinksToSpace={limitLabelLinksToSpace}
						/>
					</Box>
				)}
			</Box>
		</Box>
	);
};
